import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

const ONBOARDING_KEY_PREFIX = "qsg_onboarding_completed";

function getOnboardingKey(userId: string) {
  return `${ONBOARDING_KEY_PREFIX}_${userId}`;
}

export function useOnboardingTour() {
  const [showTour, setShowTour] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [userId, setUserId] = useState<string | null>(null);

  useEffect(() => {
    const checkUser = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) return;

      const uid = session.user.id;
      setUserId(uid);

      const completed = localStorage.getItem(getOnboardingKey(uid));
      if (!completed) {
        // Small delay to let the dashboard load first
        const timer = setTimeout(() => {
          setShowTour(true);
        }, 800);
        return () => clearTimeout(timer);
      }
    };
    checkUser();
  }, []);

  // 2 dialog steps + 8 spotlight steps = 10 total
  const TOTAL_STEPS = 10;

  const completeTour = () => {
    if (userId) {
      localStorage.setItem(getOnboardingKey(userId), "true");
    }
    setShowTour(false);
    setCurrentStep(0);
  };

  const skipTour = () => {
    if (userId) {
      localStorage.setItem(getOnboardingKey(userId), "true");
    }
    setShowTour(false);
    setCurrentStep(0);
  };

  const nextStep = () => {
    setCurrentStep((prev) => {
      const next = prev + 1;
      if (next >= TOTAL_STEPS) {
        completeTour();
        return 0;
      }
      return next;
    });
  };

  const prevStep = () => {
    setCurrentStep((prev) => Math.max(0, prev - 1));
  };

  const restartTour = () => {
    if (userId) {
      localStorage.removeItem(getOnboardingKey(userId));
    }
    setCurrentStep(0);
    setShowTour(true);
  };

  return {
    showTour,
    currentStep,
    completeTour,
    skipTour,
    nextStep,
    prevStep,
    restartTour,
  };
}
